/**
 * Live multi-buy pricing for the product page buy box.
 *
 * Reflects the quantity selector back at the shopper as a real line total,
 * struck against the undiscounted figure, plus the saving and the next tier
 * still within reach.
 *
 * Rounding matters here. Shopify's percentage discounts round the *per unit*
 * amount down to the penny and then multiply by the quantity, which is not
 * the same as taking a percentage off the line total: 4 x £14.99 at 15% is
 * £8.96 off, not £8.99. Verified against the live cart at quantities 2, 3, 4
 * and 6. Computing it any other way would print a saving the checkout does
 * not honour, which is worse than printing nothing.
 */
class GloBriteTierPrice extends HTMLElement {
  connectedCallback() {
    this.tiers = (this.dataset.tiers || '')
      .split(',')
      .filter(Boolean)
      .map((pair) => pair.split(':').map(Number))
      .filter(([qty, pct]) => qty > 0 && pct > 0)
      .sort((a, b) => a[0] - b[0]);

    this.prices = new Map(
      (this.dataset.prices || '')
        .split(',')
        .filter(Boolean)
        .map((pair) => {
          const [id, price] = pair.split(':');
          return [id, Number(price)];
        })
    );

    this.money = new Intl.NumberFormat(this.dataset.locale || 'en-GB', {
      style: 'currency',
      currency: this.dataset.currency || 'GBP',
    });

    this.scope = this.closest('product-form-component') || document;
    this.els = {
      label: this.querySelector('[data-gb-label]'),
      was: this.querySelector('[data-gb-was]'),
      now: this.querySelector('[data-gb-now]'),
      save: this.querySelector('[data-gb-save]'),
      next: this.querySelector('[data-gb-next]'),
    };

    this.update = this.update.bind(this);
    this.updateSoon = () => requestAnimationFrame(this.update);

    // The stepper buttons mutate the input's value directly, and Horizon does
    // not consistently emit an event for it, so the click is watched too.
    this.scope.addEventListener('input', this.update);
    this.scope.addEventListener('change', this.update);
    this.scope.addEventListener('click', this.updateSoon);
    document.addEventListener('variant:update', this.updateSoon);

    this.update();
  }

  disconnectedCallback() {
    this.scope?.removeEventListener('input', this.update);
    this.scope?.removeEventListener('change', this.update);
    this.scope?.removeEventListener('click', this.updateSoon);
    document.removeEventListener('variant:update', this.updateSoon);
  }

  /** Unit price in minor units for the variant currently selected, or null. */
  get unitPrice() {
    const id = this.scope.querySelector?.('input[name="id"]')?.value;
    if (id && this.prices.has(id)) return this.prices.get(id);
    // Single-variant products still have one entry; fall back to it rather
    // than hiding a block the shopper would otherwise see.
    return this.prices.size === 1 ? [...this.prices.values()][0] : null;
  }

  get quantity() {
    const raw = parseInt(this.scope.querySelector?.('input[name="quantity"]')?.value, 10);
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  }

  /** Highest tier percentage this quantity qualifies for. */
  percentFor(quantity) {
    let pct = 0;
    for (const [qty, tierPct] of this.tiers) if (quantity >= qty) pct = tierPct;
    return pct;
  }

  /** The next tier still ahead of this quantity, or null at the top. */
  nextTier(quantity) {
    return this.tiers.find(([qty]) => quantity < qty) || null;
  }

  update() {
    const unit = this.unitPrice;
    if (!unit) {
      this.hidden = true;
      return;
    }

    const quantity = this.quantity;
    const gross = unit * quantity;
    const percent = this.percentFor(quantity);
    const saved = Math.floor((unit * percent) / 100) * quantity;
    const net = gross - saved;

    this.els.label.textContent = `${quantity} × ${this.money.format(unit / 100)}`;
    this.els.now.textContent = this.money.format(net / 100);

    this.els.was.hidden = saved === 0;
    if (saved > 0) this.els.was.textContent = this.money.format(gross / 100);

    this.els.save.hidden = saved === 0;
    if (saved > 0) {
      this.els.save.textContent = `You save ${this.money.format(saved / 100)} (${percent}% off)`;
    }

    const next = this.nextTier(quantity);
    this.els.next.hidden = !next;
    if (next) {
      const [nextQty, nextPct] = next;
      const more = nextQty - quantity;
      this.els.next.textContent = `Add ${more} more to save ${nextPct}%`;
    }

    this.hidden = false;
  }
}

if (!customElements.get('globrite-tier-price')) {
  customElements.define('globrite-tier-price', GloBriteTierPrice);
}
